import { useProfile } from "@/hooks/useProfile";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";

interface TrialStatus {
  isTrialExpired: boolean;
  daysRemaining: number;
  trialEndDate: Date | null;
  isInTrial: boolean;
  // 寬限期相關
  isInGracePeriod: boolean;
  gracePeriodDaysRemaining: number;
  isGracePeriodExpired: boolean;
  // 來源指示
  dataSource: 'database' | 'fallback';
}

// 備用常量（當數據庫數據不可用時）
const TRIAL_DAYS = 14;
const GRACE_PERIOD_DAYS = 3;

export const useTrialStatus = (): TrialStatus => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { isSubscribed, currentPlan } = useSubscription();

  // 付費用戶不適用試用邏輯
  if (isSubscribed && currentPlan !== "free") {
    return {
      isTrialExpired: false,
      daysRemaining: 0,
      trialEndDate: null,
      isInTrial: false,
      isInGracePeriod: false,
      gracePeriodDaysRemaining: 0,
      isGracePeriodExpired: false,
      dataSource: 'database',
    };
  }

  const now = new Date();

  // 優先使用資料庫中的試用日期
  if (profile?.trial_expires_at && profile?.grace_period_expires_at) {
    const trialEndDate = new Date(profile.trial_expires_at);
    const gracePeriodEndDate = new Date(profile.grace_period_expires_at);
    
    const msRemaining = trialEndDate.getTime() - now.getTime();
    const daysRemaining = Math.max(0, Math.ceil(msRemaining / (24 * 60 * 60 * 1000)));
    
    const msGraceRemaining = gracePeriodEndDate.getTime() - now.getTime();
    const gracePeriodDaysRemaining = Math.max(0, Math.ceil(msGraceRemaining / (24 * 60 * 60 * 1000)));
    
    const isTrialExpired = now > trialEndDate;
    const isInTrial = !isTrialExpired && daysRemaining > 0;
    const isInGracePeriod = isTrialExpired && now <= gracePeriodEndDate;
    const isGracePeriodExpired = now > gracePeriodEndDate;

    return {
      isTrialExpired,
      daysRemaining,
      trialEndDate,
      isInTrial,
      isInGracePeriod,
      gracePeriodDaysRemaining,
      isGracePeriodExpired,
      dataSource: 'database',
    };
  }

  // 備用方案：使用 user.created_at 計算（當資料庫欄位尚未填充時）
  if (!user?.created_at) {
    return {
      isTrialExpired: false,
      daysRemaining: TRIAL_DAYS,
      trialEndDate: null,
      isInTrial: false,
      isInGracePeriod: false,
      gracePeriodDaysRemaining: GRACE_PERIOD_DAYS,
      isGracePeriodExpired: false,
      dataSource: 'fallback',
    };
  }

  const createdAt = new Date(user.created_at);
  const trialEndDate = new Date(createdAt.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
  const gracePeriodEndDate = new Date(trialEndDate.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);
  
  const msRemaining = trialEndDate.getTime() - now.getTime();
  const daysRemaining = Math.max(0, Math.ceil(msRemaining / (24 * 60 * 60 * 1000)));
  
  const msGraceRemaining = gracePeriodEndDate.getTime() - now.getTime();
  const gracePeriodDaysRemaining = Math.max(0, Math.ceil(msGraceRemaining / (24 * 60 * 60 * 1000)));
  
  const isTrialExpired = daysRemaining <= 0;
  const isInTrial = !isTrialExpired && daysRemaining > 0;
  const isInGracePeriod = isTrialExpired && gracePeriodDaysRemaining > 0;
  const isGracePeriodExpired = isTrialExpired && gracePeriodDaysRemaining <= 0;

  return {
    isTrialExpired,
    daysRemaining,
    trialEndDate,
    isInTrial,
    isInGracePeriod,
    gracePeriodDaysRemaining,
    isGracePeriodExpired,
    dataSource: 'fallback',
  };
};
